import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, ChevronDown, Eye, Phone, FileText, History,
  Copy, Truck, Filter, Download, MoreHorizontal, RefreshCw, User, X,
  Calendar, Tag, Layout, CreditCard
} from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { ordersApi } from '../services/api';
import { formatDate, formatCurrency, maskPhone } from '../utils/helpers';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import { usePermissions} from '../hooks/Usepermissions';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import DateRangeFilter from '../components/DateRangeFilter';

// ─── Auth Helper ─────────────────────────────────────────────────────────────
const getAuthUser = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) { try { return JSON.parse(userStr); } catch (e) { return null; } }
  return null;
};

const getStatusKey = (s) => String(s || '').trim().toUpperCase().replace(/[-\s]/g, '_');

// ─── AWB Mask ─────────────────────────────────────────────────────────────────
const maskAwbNumber = (awb) => {
  if (!awb || awb === '-') return '-';
  const awbStr = String(awb);
  if (awbStr.length <= 4) return awbStr;
  return 'x'.repeat(awbStr.length - 4) + awbStr.slice(-4);
};

const COURIER_APIS = {
  'E-kart': { name: 'E-kart', trackingUrl: 'https://ekart-api.flipkart.com/track/' },
  'Fship': { name: 'Fship', trackingUrl: 'https://shipping-api.com/track/' },
  'Shadowfax': { name: 'Shadowfax', trackingUrl: 'https://sfxunifiedapi.shadowfax.in/track/' },
  'Delhivery': { name: 'Delhivery', trackingUrl: 'https://track.delhivery.com/p/' }
};

// ─── StatusBadge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';

  const statusStyles = {
    'IN_TRANSIT':           { bg: isDark ? 'bg-teal-500/10' : 'bg-teal-50',   border: isDark ? 'border-teal-500/20' : 'border-teal-200',   label: 'In-Transit' },
    'DELIVERY_DELAYED':     { bg: isDark ? 'bg-red-500/10' : 'bg-red-50',    border: isDark ? 'border-red-500/20' : 'border-red-200',    label: 'Delivery delayed' },
    'DELIVERY_ATTEMPTED':   { bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50', border: isDark ? 'border-orange-500/20' : 'border-orange-200', label: 'Delivery attempted' },
    'MISROUTED':            { bg: isDark ? 'bg-pink-500/10' : 'bg-pink-50',   border: isDark ? 'border-pink-500/20' : 'border-pink-200',   label: 'Misrouted' },
    'ON_HOLD':              { bg: isDark ? 'bg-yellow-500/10' : 'bg-yellow-50', border: isDark ? 'border-yellow-500/20' : 'border-yellow-200', label: 'On-Hold' },
    'SHIPPED':              { bg: isDark ? 'bg-green-500/10' : 'bg-green-50',  border: isDark ? 'border-green-500/20' : 'border-green-200',  label: 'Shipped' },
  };
  const key = getStatusKey(status);
  const s = statusStyles[key] || { bg: isDark ? 'bg-gray-800/50' : 'bg-gray-50', border: isDark ? 'border-gray-700' : 'border-gray-200', label: status };

  return (
    <span
      className={`px-3 py-1.5 rounded-full text-[10px] desktop:text-xs font-bold border uppercase tracking-wider ${s.bg} ${s.border} whitespace-nowrap`}
      style={{ color: currentTheme.color }}
    >
      {s.label}
    </span>
  );
};

// ─── ActionMenu ───────────────────────────────────────────────────────────────
const ActionMenu = ({ orderId, onClose, onOrderDetails, onOrderHistory }) => {
  const menuRef = useRef(null);
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  return (
    <div ref={menuRef} className={`absolute right-0 top-8 rounded-lg shadow-xl border py-2 z-50 min-w-[160px] ${isDark ? 'bg-[#1f2937] border-white/10' : 'bg-white border-gray-200'}`}>
      <button onClick={() => onOrderDetails(orderId)} className={`w-full px-4 py-2.5 text-left text-sm flex items-center space-x-3 ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}>
        <FileText className="w-4 h-4 text-gray-400" /><span>Order details</span>
      </button>
      <button onClick={() => onOrderHistory(orderId)} className={`w-full px-4 py-2.5 text-left text-sm flex items-center space-x-3 ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}>
        <History className="w-4 h-4 text-gray-400" /><span>Order History</span>
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
function InTransit() {
  const { currentTheme } = useTheme();
  const { has, isAdmin } = usePermissions(); // ✅ RBAC hook
  const navigate = useNavigate();
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourier, setSelectedCourier] = useState('All courier');
  const [selectedStatus, setSelectedStatus] = useState('All status');
  const [selectedPartner, setSelectedPartner] = useState('Couriers partner');
  const [ordersPerPage, setOrdersPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const tableContainerRef = useRef(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const emptyFilters = {
    date: '', customStartDate: '', customEndDate: '',
    agent: '', category: '', paymentMode: '', campaign: '',
    status: '', courier: '', cPartner: '', teamLead: ''
  };
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  const courierOptions = ['All courier', 'E-kart', 'Fship', 'Shadowfax', 'Delhivery'];
  const statusOptions = [
    { label: 'All status', value: 'All status' },
    { label: 'In-Transit', value: 'IN_TRANSIT' },
    { label: 'Delivery delayed', value: 'DELIVERY_DELAYED' },
    { label: 'Delivery attempted', value: 'DELIVERY_ATTEMPTED' },
    { label: 'Misrouted', value: 'MISROUTED' },
    { label: 'On-Hold', value: 'ON_HOLD' },
    { label: 'Shipped', value: 'SHIPPED' }
  ];
  const partnerOptions = ['Couriers partner', 'E-kart', 'Fship', 'Shadowfax', 'Delhivery'];
  const ordersPerPageOptions = [10, 20, 50, 100];

  useEffect(() => {
    const user = getAuthUser();
    setCurrentUser(user);
    setUserLoaded(true);
  }, []);

  useEffect(() => {
  if (userLoaded) fetchInTransitOrders(1);
  setCurrentPage(1);
}, [ordersPerPage, selectedCourier, selectedStatus, selectedPartner, activeFilters, userLoaded]);
  useEffect(() => {
    const timer = setTimeout(() => { if (userLoaded) fetchInTransitOrders(); }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, userLoaded]);

  const fetchInTransitOrders = async (page = currentPage) =>{
    try {
      setLoading(true);
       const params = {
        page: page - 1,
        size: ordersPerPage,   // ✅ size bhi add karo
        limit: ordersPerPage
    };

      // Top bar filters
      if (selectedCourier !== 'All courier') params.courier = selectedCourier;
      if (selectedStatus !== 'All status') params.status = selectedStatus;
      if (selectedPartner !== 'Couriers partner') params.courierPartner = selectedPartner;
      if (searchQuery.trim()) params.searchTerm = searchQuery.trim();

      // Modal filters - move to backend params
      if (activeFilters.category) params.category = activeFilters.category;
      if (activeFilters.paymentMode) params.paymentMode = activeFilters.paymentMode;
      if (activeFilters.campaign) params.campaign = activeFilters.campaign;

      // Syncing modal status/courier/partner with top bar for server request
      if (activeFilters.status && selectedStatus === 'All status') params.status = activeFilters.status;
      if (activeFilters.courier && selectedCourier === 'All courier') params.courier = activeFilters.courier;
      if (activeFilters.cPartner && selectedPartner === 'Couriers partner') params.courierPartner = activeFilters.cPartner;
      if (activeFilters.teamLead) params.teamLead = activeFilters.teamLead;

      // Date filter
      if (activeFilters.date && activeFilters.date !== 'all_time') {
        params.dateFilter = activeFilters.date;
        if (activeFilters.date === 'custom') {
          params.startDate = activeFilters.customStartDate;
          params.endDate = activeFilters.customEndDate;
        }
      }
let startDate, endDate;


  if (activeFilters.date && activeFilters.date !== 'all_time') {
        const now = new Date();
        const fmt = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        let startDate, endDate;
        switch (activeFilters.date) {
          case 'today':
            startDate = fmt(now); endDate = fmt(now); break;
          case 'yesterday': {
            const y = new Date(now); y.setDate(y.getDate() - 1);
            startDate = fmt(y); endDate = fmt(y); break;
          }
          case 'this_week': {
            const day = now.getDay();
            const diff = now.getDate() - day + (day === 0 ? -6 : 1);
            startDate = fmt(new Date(now.getFullYear(), now.getMonth(), diff));
            endDate = fmt(now); break;
          }
          case 'last_week': {
            const lw = new Date(now); lw.setDate(lw.getDate() - 7);
            startDate = fmt(lw); endDate = fmt(now); break;
          }
          case 'this_month':
            startDate = fmt(new Date(now.getFullYear(), now.getMonth(), 1));
            endDate = fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)); break;
          case 'last_month':
            startDate = fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1));
            endDate = fmt(new Date(now.getFullYear(), now.getMonth(), 0)); break;
          case 'this_year':
            startDate = fmt(new Date(now.getFullYear(), 0, 1));
            endDate = fmt(new Date(now.getFullYear(), 11, 31)); break;
          case 'custom':
            if (activeFilters.customStartDate) params.startDate = activeFilters.customStartDate;
            if (activeFilters.customEndDate) params.endDate = activeFilters.customEndDate; break;
          default: break;
        }
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }


      // Ensure non-admin users only see their own orders
      if (currentUser && currentUser.role !== 'ADMIN') {
        params.employeeId = currentUser.id;
      } else if (activeFilters.agent) {
        params.employeeId = activeFilters.agent;
      }

      const response = await ordersApi.getInTransitOrders(params);
      let ordersData = [], total = 0;
      if (response?.data) { ordersData = response.data; total = response.total || response.data.length; }
      else if (response?.content) { ordersData = response.content; total = response.totalElements || response.content.length; }
      else if (Array.isArray(response)) { ordersData = response; total = response.length; }

      console.log('In-Transit raw API response:', response);

      const mappedInTransitOrders = ordersData.map((order, index) => ({
        id: order.id || order.orderId || index + 1,
        orderId: order.orderId || order.orderNumber || `OD${order.id}`,
        orderDate: order.orderDate ? formatDate(order.orderDate) : order.createdAt ? formatDate(order.createdAt) : '-',
        rawDate: order.orderDate || order.createdAt || null,
        customerName: order.customerName || order.lead?.customerName || '-',
        phone: order.phone || order.contactNumber || order.lead?.contactNumber || '-',
        whatsappVerified: order.whatsappVerified || false,
        product: order.productName || order.orderItems?.[0]?.productName || '-',
        category: order.category || order.orderItems?.[0]?.category || '-',
        amount: (order.grandTotal || order.amount || 0) - (order.prepaidAmount || 0),
        payMode: order.payMode || order.paymentMode || '-',
        orderCampaign: order.leadSource || '-',
        cPartner: order.cPartner || order.courierPartner || '-',
        courier: order.courier || order.courierName || '-',
        awbNumber: order.awbNumber || order.trackingNumber || '-',
        awbNumberFull: order.awbNumber || order.trackingNumber || '-',
        status: order.status || order.transitStatus || 'In-Transit',
        orderBy: order.orderBy || order.createdByName || '-',
        orderByEmp: order.orderByEmp || order.employeeId || '-',
        teamLead: order.teamLead || order.teamLeadName || '-',
        teamLeadEmp: order.teamLeadEmp || order.teamLeadId || '-',
        remarks: order.remarks || '-'
      }));

      console.log('In-Transit mapped orders:', mappedInTransitOrders);
      setOrders(mappedInTransitOrders);
      setTotalOrders(total);
    } catch (error) {
      console.error('Error fetching in-transit orders:', error);
      setOrders([]); setTotalOrders(0);
    } finally { setLoading(false); }
  };

  const handleRefresh = () => fetchInTransitOrders();
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);
  const handleOrderDetails = (orderId) => { navigate(`/orders/amount-decrease/${orderId}`, { state: { from: location.pathname, activeTab: 'DISPATCHED' } }); setActiveActionMenu(null); };
  const handleOrderHistory = (orderId) => { navigate(`/orders/${orderId}/history`, { state: { from: location.pathname } }); setActiveActionMenu(null); };

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (orders.length === 0) return;
    const headers = ['Order ID', 'Date', 'Customer', 'Phone', 'Product', 'Category', 'Amount', 'Pay Mode', 'Campaign', 'AWB', 'Status', 'Order By'];
    const csvRows = orders.map(o => [
      `"${o.orderId}"`, `"${o.orderDate}"`, `"${o.customerName}"`, `"${o.phone}"`,
      `"${o.product}"`, `"${o.category}"`, o.amount, `"${o.payMode}"`,
      `"${o.orderCampaign}"`, `"${o.awbNumberFull}"`, `"${o.status}"`, `"${o.orderBy}"`
    ].join(','));
    const blob = new Blob(['\uFEFF' + headers.join(',') + '\n' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'InTransit_Orders.csv');
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const totalPages = Math.ceil(totalOrders / ordersPerPage);
  const showingFrom = totalOrders > 0 ? (currentPage - 1) * ordersPerPage + 1 : 0;
  const showingTo = Math.min(currentPage * ordersPerPage, totalOrders);

  const getPageNumbers = () => {
    const pages = [], max = 5;
    if (totalPages <= max) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3) { for (let i = 1; i <= max; i++) pages.push(i); }
    else if (currentPage >= totalPages - 2) { for (let i = totalPages - max + 1; i <= totalPages; i++) pages.push(i); }
    else { for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i); }
    return pages;
  };

  const uniqueCategories = [...new Set(orders.map(o => o.category).filter(Boolean))];
  const uniqueAgents = [...new Set(orders.map(o => o.orderBy).filter(Boolean))];
  const uniqueCampaigns = [...new Set(orders.map(o => o.orderCampaign).filter(Boolean))];
  const uniqueStatuses = [...new Set(orders.map(o => o.status).filter(Boolean))];
  const uniqueCouriers = [...new Set(orders.map(o => o.courier || o.courierName).filter(Boolean))];
  const uniqueCPartners = [...new Set(orders.map(o => o.cPartner || o.courierPartner).filter(Boolean))];
  const uniqueTeamLeads = [...new Set(orders.map(o => o.teamLead).filter(Boolean))];

  const filteredOrders = orders.filter(order => {
    // Top bar & Modal Status Filter Integration
    const effectiveStatus = selectedStatus !== 'All status' ? selectedStatus : activeFilters.status;
    if (effectiveStatus && effectiveStatus !== '' && effectiveStatus !== 'All status') {
      const orderSt = getStatusKey(order.status);
      const filterSt = getStatusKey(effectiveStatus);
      if (orderSt !== filterSt) return false;
    }

    // Top bar & Modal Courier Filter Integration
    const effectiveCourier = selectedCourier !== 'All courier' ? selectedCourier : activeFilters.courier;
    if (effectiveCourier && effectiveCourier !== '' && effectiveCourier !== 'All courier') {
      if (order.courier !== effectiveCourier) return false;
    }

    // Top bar & Modal Partner Filter Integration
    const effectivePartner = selectedPartner !== 'Couriers partner' ? selectedPartner : activeFilters.cPartner;
    if (effectivePartner && effectivePartner !== '' && effectivePartner !== 'Couriers partner') {
      if (order.cPartner !== effectivePartner) return false;
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(order.orderId?.toLowerCase().includes(q) || order.customerName?.toLowerCase().includes(q) ||
        order.phone?.toLowerCase().includes(q) || order.product?.toLowerCase().includes(q) ||
        order.awbNumberFull?.toLowerCase().includes(q))) return false;
    }
    if (activeFilters.date && activeFilters.date !== 'all_time') {
      const raw = order.rawDate; if (!raw) return false;
      const orderDate = new Date(raw), now = new Date();
      const sod = (d) => { d.setHours(0, 0, 0, 0); return d; };
      const eod = (d) => { d.setHours(23, 59, 59, 999); return d; };
      let start, end;
      switch (activeFilters.date) {
        case 'today': start = sod(new Date()); end = eod(new Date()); break;
        case 'yesterday': const y = new Date(); y.setDate(y.getDate() - 1); start = sod(new Date(y)); end = eod(new Date(y)); break;
        case 'this_week': const dw = now.getDay(), df = now.getDate() - dw + (dw === 0 ? -6 : 1); start = sod(new Date(now.setDate(df))); end = eod(new Date()); break;
        case 'last_week': start = sod(new Date(now.setDate(now.getDate() - 7))); end = eod(new Date()); break;
        case 'this_month': start = sod(new Date(now.getFullYear(), now.getMonth(), 1)); end = eod(new Date(now.getFullYear(), now.getMonth() + 1, 0)); break;
        case 'last_month': start = sod(new Date(now.getFullYear(), now.getMonth() - 1, 1)); end = eod(new Date(now.getFullYear(), now.getMonth(), 0)); break;
        case 'this_year': start = sod(new Date(now.getFullYear(), 0, 1)); end = eod(new Date(now.getFullYear(), 11, 31)); break;
        case 'custom': if (activeFilters.customStartDate && activeFilters.customEndDate) { start = sod(new Date(activeFilters.customStartDate)); end = eod(new Date(activeFilters.customEndDate)); } break;
        default: break;
      }
      if (start && end && (orderDate < start || orderDate > end)) return false;
    }
    if (activeFilters.agent && order.orderBy !== activeFilters.agent) return false;
    if (activeFilters.category && order.category !== activeFilters.category) return false;
    if (activeFilters.paymentMode && (order.payMode || '').toUpperCase() !== activeFilters.paymentMode.toUpperCase()) return false;
    if (activeFilters.campaign && order.orderCampaign !== activeFilters.campaign) return false;
    if (activeFilters.teamLead && order.teamLead !== activeFilters.teamLead) return false;
    return true;
  });

  // Columns — ACTION only for edit-permitted users
  const tableHeaders = ['Order ID', 'Date', 'Name', 'Product', 'Category', 'Amount', 'Pay Mode', 'Order Campaign', 'C.Partner', 'AWB No.', 'Status', 'Order By', 'Team Lead', 'Remarks', ...(has('IN_TRANSIT_UPDATE_STATUS') ? ['Action'] : [])];

  const isDark = currentTheme.textColor === '#FFFFFF';

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>

      {/* ── Filter Modal ─────────────────────────────────────────────────────── */}
      {isFilterModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsFilterModalOpen(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] p-4 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-full">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-auto flex flex-col shadow-xl my-8">
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ background: currentTheme.color }}>
                <h2 className="text-lg font-bold text-white">Filter Orders</h2>
                <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5 text-white" /></button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <DateRangeFilter
                      label="Date Range"
                      preset={tempFilters.date}
                      startDate={tempFilters.customStartDate}
                      endDate={tempFilters.customEndDate}
                      onChange={({ preset, startDate, endDate }) =>
                        setTempFilters({
                          ...tempFilters,
                          date: preset,
                          customStartDate: startDate,
                          customEndDate: endDate,
                        })
                      }
                    />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Agents</label><ThemeSelect value={tempFilters.agent} onChange={(val) => setTempFilters({ ...tempFilters, agent: val })} options={[{ label: 'All Agents', value: '' }, ...uniqueAgents.map(a => ({ label: a, value: a }))]} icon={User} className="w-full" placeholder="All Agents" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Category</label><ThemeSelect value={tempFilters.category} onChange={(val) => setTempFilters({ ...tempFilters, category: val })} options={[{ label: 'All Categories', value: '' }, ...uniqueCategories.map(c => ({ label: c, value: c }))]} icon={Tag} className="w-full" placeholder="All Categories" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label><ThemeSelect value={tempFilters.paymentMode} onChange={(val) => setTempFilters({ ...tempFilters, paymentMode: val })} options={[{ label: 'All Modes', value: '' }, { label: 'COD', value: 'COD' }, { label: 'Prepaid', value: 'PREPAID' }]} icon={CreditCard} className="w-full" placeholder="All Modes" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label><ThemeSelect value={tempFilters.campaign} onChange={(val) => setTempFilters({ ...tempFilters, campaign: val })} options={[{ label: 'All Campaigns', value: '' }, ...uniqueCampaigns.map(c => ({ label: c, value: c }))]} icon={Layout} className="w-full" placeholder="All Campaigns" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Status</label><ThemeSelect value={tempFilters.status} onChange={(val) => setTempFilters({ ...tempFilters, status: val })} options={[{ label: 'All Statuses', value: '' }, ...uniqueStatuses.map(s => ({ label: s, value: s }))]} icon={Tag} className="w-full" placeholder="All Statuses" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Courier</label><ThemeSelect value={tempFilters.courier} onChange={(val) => setTempFilters({ ...tempFilters, courier: val })} options={[{ label: 'All Couriers', value: '' }, ...uniqueCouriers.map(c => ({ label: c, value: c }))]} icon={Truck} className="w-full" placeholder="All Couriers" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">C. Partner</label><ThemeSelect value={tempFilters.cPartner} onChange={(val) => setTempFilters({ ...tempFilters, cPartner: val })} options={[{ label: 'All Partners', value: '' }, ...uniqueCPartners.map(c => ({ label: c, value: c }))]} icon={Truck} className="w-full" placeholder="All Partners" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Team Lead</label><ThemeSelect value={tempFilters.teamLead} onChange={(val) => setTempFilters({ ...tempFilters, teamLead: val })} options={[{ label: 'All Team Leads', value: '' }, ...uniqueTeamLeads.map(t => ({ label: t, value: t }))]} icon={User} className="w-full" placeholder="All Team Leads" /></div>
                </div>
              </div>
              <div className="flex justify-end px-6 py-4 border-t bg-gray-50 space-x-3 rounded-b-xl">
                <button onClick={() => { setTempFilters(emptyFilters); setActiveFilters(emptyFilters); setCurrentPage(1); setIsFilterModalOpen(false); }} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Reset</button>
                <button onClick={() => { setActiveFilters(tempFilters); setCurrentPage(1); setIsFilterModalOpen(false); }} className="px-6 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: currentTheme.color }}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>In-Transit</h1>
          <span className="text-gray-500 text-lg desktop:text-2xl">({totalOrders.toLocaleString()})</span>
        </div>
        <div className="flex items-center space-x-3">
          {currentUser && (
            <div className={`flex items-center space-x-2 border rounded-lg px-3 py-2 desktop:px-4 desktop:py-3 ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
              <div className={`w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-teal-100'}`}>
                <User className={`w-4 h-4 desktop:w-6 desktop:h-6 ${isDark ? 'text-gray-400' : 'text-teal-600'}`} />
              </div>
              <div className="text-sm desktop:text-base">
                <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{currentUser.name}</p>
                <p className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{currentUser.role} • ID: {currentUser.id}</p>
              </div>
            </div>
          )}
          <button onClick={handleRefresh} className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
            <RefreshCw className={`w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Filter — sirf IN_TRANSIT_VIEW permission walo ko */}
          {has('IN_TRANSIT_VIEW') && (
            <button onClick={() => setIsFilterModalOpen(true)} className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
              <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500" />
            </button>
          )}

          {/* Export — sirf IN_TRANSIT_ORDER_EXPORT permission walo ko */}
          {has('IN_TRANSIT_VIEW') && (
            <ExcelExportButton
              onClick={handleExportCSV}
              isDownloading={loading}
            />
          )}
        </div>
      </div>

      {/* ── Filters Row ──────────────────────────────────────────────────────── */}
      <div className="flex items-center space-x-4 mb-6 flex-wrap gap-y-3">
        <ExpandableSearchBar searchTerm={searchQuery} onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }} placeholder="Search by lead id or customer name" />
        <ThemeSelect value={`${ordersPerPage} Orders`} onChange={(val) => { setOrdersPerPage(parseInt(val)); setCurrentPage(1); }} options={ordersPerPageOptions.map(o => `${o} Orders`)} className="w-44 desktop:w-60" />
        <ThemeSelect value={selectedCourier} onChange={(val) => { setSelectedCourier(val); setActiveFilters(prev => ({ ...prev, courier: '' })); setCurrentPage(1); }} options={courierOptions} icon={Truck} className="w-48 desktop:w-64" />
        <ThemeSelect value={selectedStatus} onChange={(val) => { setSelectedStatus(val); setActiveFilters(prev => ({ ...prev, status: '' })); setCurrentPage(1); }} options={statusOptions} icon={RefreshCw} className="w-52 desktop:w-72" />
        <ThemeSelect value={selectedPartner} onChange={(val) => { setSelectedPartner(val); setActiveFilters(prev => ({ ...prev, cPartner: '' })); setCurrentPage(1); }} options={partnerOptions} icon={User} className="w-56 desktop:w-80" />
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
        <div ref={tableContainerRef} className="overflow-x-auto">
          <table className="w-full min-w-[1800px]">
            <thead className="text-white" style={{ background: currentTheme.color }}>
              <tr>
                {tableHeaders.map(h => (
                  <th key={h} className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={tableHeaders.length} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-500">Loading orders...</span>
                  </div>
                </td></tr>
              ) : filteredOrders.length === 0 ? (
                <tr><td colSpan={tableHeaders.length} className="px-4 py-12 text-center text-gray-500">
                  <Truck className="w-12 h-12 text-gray-300 mx-auto mb-2" /><p>No in-transit orders found</p>
                </td></tr>
              ) : (
                filteredOrders.map((order, idx) => (
                  <tr key={order.id} className={`transition-colors ${isDark ? (idx % 2 === 0 ? 'bg-[#111827]' : 'bg-white/[0.02]') + ' hover:bg-white/5' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') + ' hover:bg-gray-50'}`}>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.orderId}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'} whitespace-nowrap`}>{order.orderDate}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                      <div className="flex flex-col">
                        <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.customerName}</span>
                        <div className="flex items-center space-x-1.5 mt-0.5">
                          <Phone className="w-3.5 h-3.5 text-gray-500" />
                          <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.phone}</span>
                          {order.whatsappVerified && <div className="w-4 h-4 bg-green-500 rounded-full" />}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate block max-w-[150px]`}>{order.product}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{order.category}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className="text-sm desktop:text-base font-bold" style={{ color: currentTheme.color }}>₹ {order.amount.toLocaleString()}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.payMode}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.orderCampaign}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.cPartner}</span></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                      <div className={`flex items-center space-x-2 w-fit px-3 py-1.5 rounded-lg transition-all ${isDark ? 'bg-white/5 border border-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`}>
                        <a
                          href={`${COURIER_APIS[order.courier]?.trackingUrl || '#'}${order.awbNumberFull}`}
                          target="_blank" rel="noopener noreferrer"
                          className={`text-sm desktop:text-base hover:underline font-mono font-bold ${isDark ? 'text-white' : ''}`}
                          style={{ color: isDark ? undefined : currentTheme.color }}
                          title={`Full AWB: ${order.awbNumberFull}`}
                        >
                          {maskAwbNumber(order.awbNumber)}
                        </a>
                        <button onClick={() => copyToClipboard(order.awbNumberFull)} className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-white'}`} title="Copy full AWB">
                          <Copy className={`w-3.5 h-3.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        </button>
                        <button onClick={() => setActiveActionMenu(activeActionMenu === order.id ? null : order.id)} className="p-2 desktop:p-3 hover:bg-gray-100 rounded-full">
                          <MoreHorizontal className="w-4 h-4 desktop:w-5 desktop:h-5 text-gray-500" />
                        </button>
                        {activeActionMenu === order.id && (
                          <ActionMenu orderId={order.id} onClose={() => setActiveActionMenu(null)} onOrderDetails={handleOrderDetails} onOrderHistory={handleOrderHistory} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5"><StatusBadge status={order.status} /></td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                      <div className="flex flex-col">
                        <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.orderBy}</span>
                        <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.orderByEmp}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                      <div className="flex flex-col">
                        <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.teamLead}</span>
                        <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.teamLeadEmp}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                      <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-700'} block max-w-[200px]`}
                        style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        title={order.remarks}>{order.remarks}</span>
                    </td>
                    {has('IN_TRANSIT_UPDATE_STATUS') && (
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex items-center justify-center space-x-2 relative">
                          <button
                            onClick={() => handleOrderDetails(order.id)}
                            className={`p-2 desktop:p-3 rounded-full transition-colors hover:shadow-md ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                            style={{ backgroundColor: `${currentTheme.color}20`, color: currentTheme.color }}
                          >
                            <Eye className="w-4 h-4 desktop:w-5 desktop:h-5" />
                          </button>
                          <button onClick={() => setActiveActionMenu(activeActionMenu === order.id ? null : order.id)} className={`p-2 desktop:p-3 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                            <MoreHorizontal className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                          </button>
                          {activeActionMenu === order.id && (
                            <ActionMenu orderId={order.id} onClose={() => setActiveActionMenu(null)} onOrderDetails={handleOrderDetails} onOrderHistory={handleOrderHistory} />
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4">
        <Pagination
  currentPage={currentPage}
  totalItems={totalOrders}
  itemsPerPage={ordersPerPage}
  onPageChange={(page) => {
    setCurrentPage(page);
    fetchInTransitOrders(page);
  }}
/>
        </div>
      </div>
    </div>
  );
}

export default InTransit;